import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import JsonLd from '@/components/feature/JsonLd';
import VideoCard from '@/components/base/VideoCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || 'gaoxiao');
  const [activeSlug, setActiveSlug] = useState(slug || 'gaoxiao');
  const videos = getVideosByCategory(activeSlug);
  const displayCategory = getCategoryBySlug(activeSlug);

  if (!displayCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">分类不存在</p>
          <Link to="/" className="text-[oklch(var(--primary-500))] text-sm mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/gaoxiao' },
    { label: displayCategory.name },
  ];

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayCategory.name} - 香蕉视频分类`,
    description: `${displayCategory.description}，共${displayCategory.videoCount}个视频`,
    url: `https://www.bananavideo.cn/category/${activeSlug}`,
  };

  return (
    <>
      <ScrollToTop />
      <JsonLd data={collectionJsonLd} />

      <header>
        <Navbar />
      </header>

      <main className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-8">
          <div className="flex items-center gap-4 mb-8">
            <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center ${displayCategory.color}`}>
              <i className={`${displayCategory.icon} text-2xl md:text-3xl`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {displayCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                {displayCategory.description} · 共 {displayCategory.videoCount.toLocaleString()} 个视频
              </p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveSlug(cat.slug)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer border ${
                  activeSlug === cat.slug
                    ? 'bg-[oklch(var(--primary-500))] text-background-50 border-[oklch(var(--primary-500))]'
                    : 'bg-background-50 text-foreground-600 border-background-200 hover:border-[oklch(var(--primary-500)/0.4)]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </section>

        {/* Videos Grid */}
        <section className="w-full px-4 md:px-6 pb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {videos.map((video) => (
              <VideoCard
                key={video.id}
                id={video.id}
                title={video.title}
                thumbnail={video.thumbnail}
                authorName={video.authorName}
                authorAvatar={video.authorAvatar}
                views={video.views}
                likes={video.likes}
                duration={video.duration}
                categoryName={video.categoryName}
                categorySlug={video.categorySlug}
              />
            ))}
          </div>

          {videos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-video-line text-foreground-300 text-3xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无视频</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}